/* eslint-disable max-lines */
/* eslint-disable react/no-array-index-key */
import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { mediaQuery, Spacings } from '@styles/index';
import Typography from 'aquilla/core/atoms/Typography';
import CheckContained from 'aquilla/icons/misc/CheckContained';
import Image from '@base/Image';
import Button from 'aquilla/core/molecules/Button';
import Divider from 'aquilla/core/atoms/Divider';
import { segment } from '@actions/analytics';
import { commasIndianFormat, currencySymbolMap } from '@utils/textHelper';
import useGoalEventProps from '@hooks/useGoalEventProps';
import pushRoute from '@utils/pushRoute';
import { useRouter } from 'next/router';
import { CHECKOUT_SUBSCRIPTION_TYPE_SELECTED } from '@cont/Checkout/constants/segment';
import { SUBSCRIPTION_PLAN_CLICKED } from '@constants/segment';
import IconicMobile from 'aquilla/icons/subscriptions/IconicMobile';
import PlusMobile from 'aquilla/icons/subscriptions/PlusMobile';
import LiteMobile from 'aquilla/icons/subscriptions/LiteMobile';
import useSubsriptionFeatures from '@api/hooks/goal/useSubscriptionBenefits';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import { LOGIN_DRAWER } from '@constants/toggleables';
import { useToggleable } from '@stores/toggleables';
import { plusSubscriptionTiles } from '@cont/NewPreSubscribed/constants';
import Syllabus from 'aquilla/icons/subscriptions/Syllabus';
import TopEducatorsMweb from 'aquilla/icons/subscriptions/TopEducators';
import BestInClass from 'aquilla/icons/subscriptions/BestInClass';
import LMP from 'aquilla/icons/subscriptions/LMP';
import isEmptyObject from '@utils/isEmptyObject';
import useGoalInfo from '@api/hooks/goal/useGoalInfo';
import { getIsPrimeEnabled } from '@api/selectors/topology';
import SubscriptionTilesComponent from '../SubscriptionTiles';
import {
  getPlanAvailability,
  getSubcriptionTypeCount,
  subscriptionFeaturesAvailable
} from './utils';

const Container = styled.div`
  display: none;
  ${mediaQuery.uptoMobile} {
    display: block;
  }
`;
const LabelWrapper = styled.div`
  display: flex;
  margin-bottom: ${Spacings.SPACING_6B};
  padding: 14px ${Spacings.SPACING_1B} ${Spacings.SPACING_2B};
  justify-content: space-between;
  border-radius: ${Spacings.SPACING_2B};
  overflow: scroll;
  ::-webkit-scrollbar {
    display: none;
  }
  > div {
    flex: 0 0 25%;
    display: flex;
  }
`;
const BenefitPriceWrapper = styled.div`
  ${mediaQuery.uptoMobile} {
    flex-basis: 100% !important;

    + * {
      flex-shrink: 0;
    }
  }
`;
const StyledFreeButton = styled(Button)`
  width: 141px;
`;
const StyledButton = styled(Button)`
  width: 150px;
  background-color: ${(props) => props.color};
  color: var(--color-i-white);
  border: none;

  :hover,
  :focus {
    background-color: ${(props) => props.color};
  }
  .bounce {
    background-color: var(--color-i-white);
  }
`;
const StyledLabelButton = styled(Button)`
  background-color: transparent;
  border: none;
  padding: 0 ${Spacings.SPACING_2B};
  color: ${({ $color }) => $color};
  font-size: initial;
  height: ${Spacings.SPACING_10B};
  width: 100%;
  cursor: initial;
  pointer-events: initial;
  &.selected {
    background-color: var(--color-i-white);
    border-radius: ${Spacings.SPACING_1B};
  }
  .button--right-icon {
    display: inline-flex !important;
  }
`;
const ItemWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 9px;
`;
const ColumnWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-basis: 100%;
  gap: ${Spacings.SPACING_4B};
  margin: 24px 0 16px;
  padding-left: ${Spacings.SPACING_1B};
  &.hide {
    display: none;
  }
`;
const GetSubscriptionItemWrapper = styled.div`
  display: flex;
  gap: 15px;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-end;
  align-self: flex-end;
  flex-basis: 100%;
  &.hide {
    display: none;
  }
  &.free {
    justify-content: center;
  }
`;
const GetSubScriptionSec = styled.div`
  display: flex;
  padding: 19px 0 0;
  height: 65px;
`;
const GoalHeadingWrapper = styled.div`
  gap: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NewTag = styled.span`
  position: absolute;
  top: 0;
  right: 0;
  transform: translate(0%, -55%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 16px;
  width: 36px;
  border-radius: 17px;
  background-color: var(--color-i-red);
  color: var(--color-i-white);
  font-size: 10px;
  line-height: 16px;
  font-weight: 700;
  text-transform: uppercase;
  white-space: nowrap;
`;

const LabelWithTag = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;

  ${StyledLabelButton} {
    width: auto;
  }
`;
const FeatureTable = styled.div`
  align-items: center;
  gap: ${Spacings.SPACING_4B};
  margin: ${Spacings.SPACING_5B} 0;
  padding: ${Spacings.SPACING_5B} ${Spacings.SPACING_4B};
  position: relative;
  width: 100%;
  &[aria-colcount='3'] {
    ${GoalHeadingWrapper} {
      flex-basis: calc(100% / 2);
    }
  }
  &[aria-colcount='4'] {
    ${GoalHeadingWrapper} {
      flex-basis: calc(100% / 3);
    }
  }
  &[aria-colcount='5'] {
    ${GoalHeadingWrapper} {
      flex-basis: calc(100% / 4);
    }
  }
  &[aria-colcount='6'] {
    ${GoalHeadingWrapper} {
      flex-basis: calc(100% / 5);
    }
  }
  &[aria-label='iconic_pro'] {
    background: #ececfa87;

    ${LabelWrapper} {
      background: #ececfa;
    }
  }
  &[aria-label='iconic'] {
    background: #fff7ef;

    ${LabelWrapper} {
      background: #fdebda;
    }
  }
  &[aria-label='plus'] {
    background: #f7f9fb;

    ${LabelWrapper} {
      background: #e5ebf1;
    }
  }
  &[aria-label='lite'] {
    background: #f0fbff;

    ${LabelWrapper} {
      background: #ddf2f9;
    }
  }
  &[aria-label='free'] {
    background: #f7f9fb;

    ${LabelWrapper} {
      background: #e5ebf1;
    }
  }
  &[aria-label='offline'] {
    background: #e4ebf1;

    ${LabelWrapper} {
      background: #f0f8ff;
    }
  }
`;
const ColoredDivider = styled(Divider)`
  border-color: ${({ $color }) => $color};
`;
const TileContainer = styled.div`
  width: 100%;
  padding: ${Spacings.SPACING_4B};
  margin: ${Spacings.SPACING_6B} auto ${Spacings.SPACING_5B};
`;
const TilesHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacings.SPACING_3B};
  margin-bottom: ${Spacings.SPACING_2B};
`;
const TilesSubHeader = styled.div`
  margin-bottom: ${Spacings.SPACING_10B};
`;

const PrimeBenefitText = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const PrimeLogo = styled(Image)`
  height: 16px;
  width: auto;
`;

const getDividerColor = (plan) => {
  switch (plan) {
    case 'iconic_pro':
      return '#E9DED4';
    case 'iconic':
      return '#E9DED4';
    case 'plus':
    case 'lite':
      return '#D5E1EA';
    default:
      return 'var(--color-i-divider)';
  }
};

const getFirstAvailablePlan = (
  isIconicProAvailable,
  isIconicAvailable,
  isPlusAvailable,
  isLiteAvailable
) => {
  if (isIconicProAvailable) return 'iconic_pro';
  if (isIconicAvailable) return 'iconic';
  if (isPlusAvailable) return 'plus';
  if (isLiteAvailable) return 'lite';
  return 'free';
};

const tileImageMappingMweb = [TopEducatorsMweb, Syllabus, BestInClass, LMP];

const SubscriptionBenefitsMWeb = (props) => {
  const { data: cardData, goalName, onSelectPlan, goal } = props;
  const {
    query: { goalSlug, goalUID }
  } = useRouter();
  const { goalInfo } = useGoalInfo(goalUID);
  const {
    isIconicAvailable,
    isIconicProAvailable,
    isPlusAvailable,
    isLiteAvailable
  } = getPlanAvailability(goal || goalInfo, cardData);
  const [selectedPlan, setPlan] = useState(
    getFirstAvailablePlan(
      isIconicProAvailable,
      isIconicAvailable,
      isPlusAvailable,
      isLiteAvailable
    )
  );
  const [typeSelected, setTypeSelected] = useState();
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);
  const goalSegmentData = useGoalEventProps({});
  const [isLoading, setIsLoading] = useState({
    lite: false,
    plus: false,
    iconic: false,
    iconic_pro: false
  });

  useEffect(() => {
    const isSelectedPlanAvailable =
      (selectedPlan === 'iconic_pro' && isIconicProAvailable) ||
      (selectedPlan === 'iconic' && isIconicAvailable) ||
      (selectedPlan === 'plus' && isPlusAvailable) ||
      (selectedPlan === 'lite' && isLiteAvailable) ||
      selectedPlan === 'free';
    if (!isSelectedPlanAvailable) {
      setPlan(
        getFirstAvailablePlan(
          isIconicProAvailable,
          isIconicAvailable,
          isPlusAvailable,
          isLiteAvailable
        )
      );
    }
  }, [
    isIconicAvailable,
    isIconicProAvailable,
    isLiteAvailable,
    isPlusAvailable,
    selectedPlan
  ]);

  const handleOnClick = (label) => {
    setPlan(label);
  };

  const getSubscriptionRedirectHandler = (type) => {
    if (!onSelectPlan) {
      setIsLoading({ ...isLoading, [type]: true });
    }
    segment.track(CHECKOUT_SUBSCRIPTION_TYPE_SELECTED, {
      ...goalSegmentData,
      subscription_sub_type: 'Standard'
    });
    segment.track(SUBSCRIPTION_PLAN_CLICKED, {
      ...goalSegmentData,
      subscription_sub_type: 'Standard'
    });
    if (onSelectPlan) {
      onSelectPlan(type);
      return;
    }
    const route = {
      href: `/goal/[goalSlug]/[goalUID]/subscribe?plan_type=${type}`,
      as: `/goal/${goalSlug}/${goalUID}/subscribe?plan_type=${type}`
    };
    pushRoute(route.href, route.as);
  };

  const getSubscription = (type) => {
    if (onSelectPlan) {
      segment.track(CHECKOUT_SUBSCRIPTION_TYPE_SELECTED, {
        ...goalSegmentData,
        subscription_sub_type: 'Standard'
      });
      segment.track(SUBSCRIPTION_PLAN_CLICKED, {
        ...goalSegmentData,
        subscription_sub_type: 'Standard'
      });
      onSelectPlan(type);
      return;
    }
    if (isLoggedIn) {
      getSubscriptionRedirectHandler(type);
    } else {
      setTypeSelected(type);
      openLoginDrawer();
    }
  };

  useEffect(() => {
    if (onSelectPlan) return;
    if (isLoggedIn && typeSelected && typeSelected !== 'offline') {
      getSubscriptionRedirectHandler(typeSelected);
      setTypeSelected(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const { subscriptionData: cmsSubscriptionData } = useSubsriptionFeatures(
    goalUID,
    isIconicProAvailable
  );
  const isPrimeEnabled = getIsPrimeEnabled(goal || goalInfo);

  if (isEmptyObject(cmsSubscriptionData) || !cmsSubscriptionData) return null;
  const subscriptionData = cmsSubscriptionData;
  const colcount = getSubcriptionTypeCount({
    isIconicAvailable,
    isIconicProAvailable,
    isLiteAvailable,
    isPlusAvailable,
    isCentreAvailable: false
  });
  const { featureName, free, iconicPro, iconic, plus, lite } =
    subscriptionFeaturesAvailable(subscriptionData.features, isPrimeEnabled);
  const currency = currencySymbolMap();

  const Column = ({ subscription, color, type }) => (
    <ColumnWrapper className={selectedPlan !== type ? 'hide' : ''}>
      {subscription.map((isAvailable, i) => (
        <ItemWrapper key={i}>
          {isAvailable ? (
            <CheckContained width={20} height={20} color={color} />
          ) : (
            <Image src="Group.svg" width={20} height={20} alt="icon" />
          )}{' '}
          {featureName[i] === 'PRIME_BENEFITS_ROW' ? (
            <PrimeBenefitText>
              <Typography variant="h5" color="var(--color-text-primary)">
                All benefits of{' '}
              </Typography>
              <PrimeLogo
                src="prime/prime-logo.svg"
                alt="Prime"
                width={50}
                height={16}
              />
            </PrimeBenefitText>
          ) : (
            <Typography variant="h5" color="var(--color-text-primary)">
              {featureName[i]}
            </Typography>
          )}
        </ItemWrapper>
      ))}
    </ColumnWrapper>
  );
  const getLabel = (type) => {
    switch (type) {
      case 'lite':
        return 'Lite';
      case 'iconic':
        return 'Iconic';
      case 'iconic_pro':
        return 'Iconic Pro';
      default:
        return 'Plus';
    }
  };
  const getLowestPriceForPlan = (type) => {
    switch (type) {
      case 'lite':
        return cardData.liteLowestPrice;
      case 'iconic':
        return cardData.iconicLowestPrice;
      case 'iconic_pro':
        return cardData.iconicProLowestPrice;
      default:
        return cardData.plusLowestPrice;
    }
  };
  const Price = ({ color, type }) => {
    return (
      <GetSubscriptionItemWrapper
        className={selectedPlan !== type ? 'hide' : ''}
      >
        <BenefitPriceWrapper>
          <Typography variant="p2" color="var(--color-i-gray-13)">
            Starts from
          </Typography>
          <Typography variant="h5" color={color}>
            {`${currency} ${commasIndianFormat(
              Math.round(getLowestPriceForPlan(type))
            )}`}{' '}
            / month{' '}
          </Typography>
        </BenefitPriceWrapper>
        <StyledButton
          variant="secondary"
          color={color}
          label={`Get ${getLabel(type)}`}
          onClick={() => getSubscription(type)}
          isLoading={isLoading[type]}
        />
      </GetSubscriptionItemWrapper>
    );
  };

  return (
    <Container>
      {colcount < 3 && (
        <TileContainer>
          <TilesHeader>
            <PlusMobile height="32" width="32" />
            <Typography variant="h2" color="var(--color-i-black)">
              Plus subscription
            </Typography>
          </TilesHeader>
          <TilesSubHeader>
            <Typography variant="p2" color="var(--color-i-gray-13)">
              Ace your preparation with study material prepared by top educators
            </Typography>
          </TilesSubHeader>
          <SubscriptionTilesComponent
            tiles={plusSubscriptionTiles}
            tileImageMapping={tileImageMappingMweb}
            goalName={goalName}
          />
        </TileContainer>
      )}
      <FeatureTable
        aria-label={selectedPlan}
        aria-colcount={Math.max(3, colcount)}
      >
        <LabelWrapper>
          <GoalHeadingWrapper
            style={{ display: colcount < 3 ? 'flex' : 'none' }}
          >
            <StyledLabelButton
              label="Free"
              $color="#3C4852"
              className={selectedPlan === 'free' ? 'selected' : ' '}
              onClick={() => handleOnClick('free')}
            />
          </GoalHeadingWrapper>
          <GoalHeadingWrapper
            style={{ display: isLiteAvailable ? 'flex' : 'none' }}
          >
            <StyledLabelButton
              label="Lite"
              startIcon={<LiteMobile height={20} width={20} />}
              $color="#1DAFDE"
              className={selectedPlan === 'lite' ? 'selected' : ' '}
              onClick={() => handleOnClick('lite')}
            />
          </GoalHeadingWrapper>
          <GoalHeadingWrapper
            style={{ display: isPlusAvailable ? 'flex' : 'none' }}
          >
            <StyledLabelButton
              label="Plus"
              startIcon={<PlusMobile height={20} width={20} />}
              $color="#5991BB"
              className={selectedPlan === 'plus' ? 'selected' : ' '}
              onClick={() => handleOnClick('plus')}
            />
          </GoalHeadingWrapper>
          <GoalHeadingWrapper
            style={{ display: isIconicAvailable ? 'flex' : 'none' }}
          >
            <StyledLabelButton
              label="Iconic"
              startIcon={<IconicMobile height={20} width={20} />}
              $color="#EF953E"
              className={selectedPlan === 'iconic' ? 'selected' : ' '}
              onClick={() => handleOnClick('iconic')}
            />
          </GoalHeadingWrapper>
          <GoalHeadingWrapper
            style={{
              display: isIconicProAvailable ? 'flex' : 'none',
              flex: '0 0 auto'
            }}
          >
            <LabelWithTag>
              <StyledLabelButton
                label="Iconic Pro"
                startIcon={
                  <Image
                    src="bundles/IconicProMobile.svg"
                    width={20}
                    height={20}
                    alt="Iconic Pro"
                  />
                }
                $color="#4F4FD0"
                className={selectedPlan === 'iconic_pro' ? 'selected' : ' '}
                onClick={() => handleOnClick('iconic_pro')}
              />
              <NewTag>New</NewTag>
            </LabelWithTag>
          </GoalHeadingWrapper>
        </LabelWrapper>
        {colcount < 3 && (
          <Column subscription={free} color="#3C4852" type="free" />
        )}
        {isLiteAvailable && (
          <Column subscription={lite} color="#25B2DF" type="lite" />
        )}
        {isPlusAvailable && (
          <Column subscription={plus} color="#5991BB" type="plus" />
        )}
        {isIconicAvailable && (
          <Column subscription={iconic} color="#FFAD3B" type="iconic" />
        )}
        {isIconicProAvailable && (
          <Column subscription={iconicPro} color="#4F4FD0" type="iconic_pro" />
        )}
        <ColoredDivider $color={getDividerColor(selectedPlan)} />
        <GetSubScriptionSec>
          {colcount < 3 && (
            <GetSubscriptionItemWrapper
              className={selectedPlan !== 'free' ? 'free hide' : 'free'}
            >
              {!isLoggedIn ? (
                <StyledFreeButton
                  variant="secondary"
                  label="Try free"
                  onClick={openLoginDrawer}
                />
              ) : (
                <Typography variant="h6" color="var(--color-i-black)">
                  You’re on free plan
                </Typography>
              )}
            </GetSubscriptionItemWrapper>
          )}
          {isLiteAvailable && <Price type="lite" color="#25B2DF" />}
          {isPlusAvailable && <Price type="plus" color="#5F8AAC" />}
          {isIconicAvailable && <Price type="iconic" color="#FFAD3B" />}
          {isIconicProAvailable && <Price type="iconic_pro" color="#4F4FD0" />}
        </GetSubScriptionSec>
      </FeatureTable>
    </Container>
  );
};
export default SubscriptionBenefitsMWeb;
